import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, toApiError } from '@/lib/api'
import type { UsageAnalyticsResponse } from '@/lib/api'
import { useAnswerApi } from '@/hooks/useAnswerApi'

export type AnalyticsState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: UsageAnalyticsResponse }
  | { kind: 'error'; message: string }

export type UseAnalyticsOptions = {
  baseUrl: string
  acquireToken: () => Promise<string>
  /** Inclusive ISO dates (YYYY-MM-DD). */
  from: string
  to: string
  fetchImpl?: typeof fetch
  /** Gate the load on the caller being a confirmed admin — the endpoint is
   * admin-only and would 403 otherwise. */
  enabled?: boolean
}

export type UseAnalyticsResult = {
  state: AnalyticsState
  refresh: () => Promise<void>
}

export function useAnalytics(options: UseAnalyticsOptions): UseAnalyticsResult {
  const { from, to } = options
  const enabled = options.enabled ?? true
  const [state, setState] = useState<AnalyticsState>({ kind: 'loading' })
  const abortRef = useRef<AbortController | null>(null)
  const api = useAnswerApi(options)

  const refresh = useCallback(async () => {
    const controller = new AbortController()
    abortRef.current?.abort()
    abortRef.current = controller
    try {
      const data = await api.usageAnalytics(
        { from: new Date(from), to: new Date(to) },
        { signal: controller.signal },
      )
      if (controller.signal.aborted) return
      setState({ kind: 'ready', data })
    } catch (err) {
      if (controller.signal.aborted) return
      const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
      setState({ kind: 'error', message: apiErr.message })
    } finally {
      if (abortRef.current === controller) abortRef.current = null
    }
  }, [api, from, to])

  useEffect(() => {
    if (!enabled) return
    setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'loading' }))
    void refresh()
    return () => {
      abortRef.current?.abort()
    }
  }, [enabled, refresh])

  return { state, refresh }
}
